import React from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Lock, TrendingUp } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { asText, CardShell, EmptyRow, formatCompact, formatCurrency, formatPct, formatRoas, LoadingRow, Metric, readMetric, StatusBadge, unwrapValue, useCxoResource } from "../shared";

// Campaign detail's Overview tab — the one mega-endpoint per the doc:
// "series (Campaign performance over time), top_adset (Top-performing Ad Set
// card), adset_contribution, audience_snapshot, placement_performance,
// regional_performance, delivery_efficiency (frequency, CPM, CTR link, CPC
// link, cost per result + the Efficiency improving insight) and
// device_breakdown (available:false gives the 'Device data not available'
// state). last_synced_at is the 'Last synced' line."

const ShareBarList: React.FC<{ items: { label: string; value: number }[] }> = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((it, i) => (
      <div key={i}>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-600 dark:text-gray-300 truncate">{it.label}</span>
          <span className="font-semibold text-gray-900 dark:text-gray-100 shrink-0 ml-2">{it.value.toFixed(0)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.max(2, it.value)}%` }} />
        </div>
      </div>
    ))}
  </div>
);

const CampaignOverviewTab: React.FC<{ campaignId: string; baseParams: Record<string, any> }> = ({ campaignId, baseParams }) => {
  const navigate = useNavigate();
  const { data, loading } = useCxoResource<any>("/cxo-dashboard/campaigns", `${campaignId}/overview`, { ...baseParams, granularity: "daily", metric: "results" }, !!campaignId);

  if (loading && !data) return <LoadingRow label="Loading campaign overview…" />;
  if (!data) return <EmptyRow label="No overview data for this period yet." />;

  const series: any[] = Array.isArray(data.series) ? data.series : [];
  const topAdset = data.top_adset || {};
  const adsetContribution: any[] = Array.isArray(data.adset_contribution?.items) ? data.adset_contribution.items : Array.isArray(data.adset_contribution) ? data.adset_contribution : [];
  const placementPerformance: any[] = Array.isArray(data.placement_performance?.items) ? data.placement_performance.items : Array.isArray(data.placement_performance) ? data.placement_performance : [];
  const regionalPerformance: any[] = Array.isArray(data.regional_performance?.items) ? data.regional_performance.items : Array.isArray(data.regional_performance) ? data.regional_performance : [];
  const audienceSnapshot = data.audience_snapshot || {};
  const delivery = data.delivery_efficiency || {};
  const deviceBreakdown = data.device_breakdown;

  const toShareItems = (arr: any[], labelKeys: string[], valueKey = "share_pct") =>
    arr.map((it) => ({
      label: asText(labelKeys.map((k) => it[k]).find((v) => v != null) ?? it.name),
      value: Number(unwrapValue(it[valueKey] ?? it.value ?? it.spend_share_pct ?? it.impressions_share_pct)) || 0,
    }));

  const topAdsetResults: Metric = readMetric(topAdset.results);
  const topAdsetCpr: Metric = readMetric(topAdset.cost_per_result);
  const topAdsetRoas: Metric = readMetric(topAdset.roas);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardShell title="Campaign performance over time" description="Track spend, results and efficiency across the selected period." className="lg:col-span-2">
          {series.length === 0 ? (
            <EmptyRow label="No performance series for this period yet." />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series.map((s) => ({ date: asText(s.date), spend: unwrapValue(s.spend) || 0, results: unwrapValue(s.results) || 0, cost_per_result: unwrapValue(s.cost_per_result) || 0 }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar yAxisId="left" dataKey="spend" name="Spend" fill="#60a5fa" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="results" name="Results" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="cost_per_result" name="Cost per result" stroke="#d946ef" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardShell>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5 flex flex-col">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 mb-2">
            <Trophy size={14} /> Top-performing Ad Set
          </div>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{asText(topAdset.name)}</p>
            {topAdset.status && <StatusBadge status={topAdset.status} />}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mb-4">
            {[
              { label: "Results", metric: topAdsetResults, format: formatCompact, empty: "0" },
              { label: "Cost per result", metric: topAdsetCpr, format: formatCurrency, empty: "₦0.00" },
              { label: "ROAS", metric: topAdsetRoas, format: formatRoas, empty: "N/A" },
            ].map((c) => (
              <div key={c.label}>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{c.metric.value == null ? c.empty : c.format(c.metric.value)}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{c.label}</p>
              </div>
            ))}
          </div>
          {topAdset.adset_id && (
            <button
              type="button"
              onClick={() => navigate(`/cxo/campaigns/${campaignId}/adsets/${topAdset.adset_id}`)}
              className="mt-auto w-full py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
            >
              View Ad Set
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardShell title="Ad set contribution" description="Share of results by ad set.">
          {adsetContribution.length === 0 ? <EmptyRow label="No ad sets yet." /> : <ShareBarList items={toShareItems(adsetContribution, ["name"])} />}
        </CardShell>
        <CardShell title="Audience snapshot" description="Best performing audience segments.">
          {Object.keys(audienceSnapshot).length === 0 ? (
            <EmptyRow label="No audience snapshot yet." />
          ) : (
            <div className="space-y-2 text-xs">
              {[
                ["Top audience", asText(audienceSnapshot.top_audience?.name, "N/A")],
                ["Best age group", asText(audienceSnapshot.best_age_group?.age, "N/A")],
                ["Strongest gender segment", (() => { const g = asText(audienceSnapshot.strongest_gender?.gender, "N/A"); return g === "N/A" ? g : g.charAt(0).toUpperCase() + g.slice(1); })()],
                ["Highest response region", asText(audienceSnapshot.highest_response_region?.region, "N/A")],
                ["Top placement", asText(audienceSnapshot.top_placement?.placement, "N/A")],
              ].map(([label, value]) => (
                <div key={label as string} className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400">{label}</span>
                  <span className="font-semibold text-indigo-600 dark:text-indigo-400">{value}</span>
                </div>
              ))}
            </div>
          )}
        </CardShell>
        <CardShell title="Placement performance" description="Share of results by placement.">
          {placementPerformance.length === 0 ? <EmptyRow label="No placements yet." /> : <ShareBarList items={toShareItems(placementPerformance, ["platform_position", "placement"])} />}
        </CardShell>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardShell title="Regional performance" description="Top regions by results.">
          {regionalPerformance.length === 0 ? <EmptyRow label="No regions yet." /> : <ShareBarList items={toShareItems(regionalPerformance, ["region", "name"])} />}
        </CardShell>

        <CardShell title="Campaign delivery & efficiency" description="Key delivery and efficiency metrics.">
          <div className="grid grid-cols-3 gap-y-3 text-center mb-3">
            {[
              { label: "Frequency", value: delivery.frequency != null ? Number(unwrapValue(delivery.frequency)).toFixed(1) : "0.0" },
              { label: "CPM", value: formatCurrency(delivery.cpm, "₦0.00") },
              { label: "CTR (link)", value: formatPct(delivery.ctr_link ?? delivery.ctr, "0%") },
              { label: "CPC (link)", value: formatCurrency(delivery.cpc_link ?? delivery.cpc, "₦0.00") },
              { label: "Cost per result", value: formatCurrency(delivery.cost_per_result, "₦0.00") },
            ].map((c) => (
              <div key={c.label}>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{c.value}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{c.label}</p>
              </div>
            ))}
          </div>
          {delivery.insight && (
            <div className="flex items-start gap-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg px-3 py-2">
              <TrendingUp size={13} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-700 dark:text-emerald-400">{asText(delivery.insight)}</p>
            </div>
          )}
        </CardShell>

        <CardShell title="Device breakdown" description="Impressions by device.">
          {!deviceBreakdown || deviceBreakdown.available === false ? (
            <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-gray-400 dark:text-gray-500">
              <Lock size={16} />
              <p className="text-sm font-medium">Device data not available</p>
              <p className="text-xs text-center">Device breakdown is not available in the current data set.</p>
            </div>
          ) : (
            <EmptyRow label="See the Devices tab for the full breakdown." />
          )}
        </CardShell>
      </div>

      {data.last_synced_at && <p className="text-xs text-gray-400 dark:text-gray-500">Last synced: {new Date(data.last_synced_at).toLocaleString()}</p>}
    </div>
  );
};

export default CampaignOverviewTab;
